import { useEffect, useLayoutEffect, useState, useContext } from 'react';
import { LoadingContext } from '../App';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Canvas from '../components/Canvas';
import MainFooter from '../components/MainFooter';
import portfolioData from '../data/portfolio.json';
import blogData from '../data/blog.json';
import '../styles/portfolio-detail.css';

const ProjectDetail = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const location = useLocation();
    const [project, setProject] = useState<any>(null);
    const { setIsPageReady } = useContext(LoadingContext);

    useEffect(() => {
        const foundProject = portfolioData.find(p => p.projectId === projectId);
        if (foundProject) {
            setProject(foundProject);
            // Signal that the page is ready once the project data is loaded
            setIsPageReady(true);
        } else {
            // Signal ready even if not found to show fallback/404
            setIsPageReady(true);
        }
    }, [projectId, setIsPageReady]);

    useEffect(() => {
        // Add class to body for CSS targeting
        document.body.classList.add('project-detail-page');

        return () => {
            document.body.classList.remove('project-detail-page');
        };
    }, []);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
        const smoothContent = document.getElementById('smooth-content');
        if (smoothContent) {
            smoothContent.scrollTop = 0;
        }
    }, []);

    if (!project) {
        return <div className="loading-screen">Loading Project...</div>;
    }

    return (
        <div className="project-detail-page">
            <Helmet>
                <title>{`${project.hero.mainTitle} | Sacesta Portfolio`}</title>
                <meta name="description" content={project.hero.narrative} />
                <link rel="canonical" href={`https://sacesta.com/our-work/portfolio/${projectId}`} />
            </Helmet>
            <div className="cursor1"></div>
            <div className="cursor2"></div>

            <button id="scroll_top" className="scroll-top"><i className="fa-solid fa-arrow-up"></i></button>

            <header className="header__area-3">
                <div className="header__inner-3">
                    <div className="header__logo-2">
                        <Link to="/" className="logo-dark">
                            <img src="/assets/imgs/logo/sacesta-logo-black.png" style={{ width: '150px', height: '40px' }} alt="Site Logo" />
                        </Link>
                        <Link to="/" className="logo-light">
                            <img src="/assets/imgs/logo/site-logo-white-2.png" alt="Site Logo" />
                        </Link>
                    </div>
                    <div className="header__nav-2">
                        <ul className="main-menu-3 menu-anim">
                            <li><Link to="/">Home</Link></li>
                            <li><Link to="/about">About</Link></li>
                            <li><Link to="/our-work">Our Work</Link></li>
                            <li><a href="https://calendly.com/sacesta/30min">Book A Meet</a></li>
                        </ul>
                    </div>
                    <div className="header__nav-icon-3">
                        <button className="search-icon" id="search_icon"><i className="fa-solid fa-magnifying-glass"></i></button>
                        <button className="search-icon" id="search_close"><i className="fa-solid fa-xmark"></i></button>
                        <button id="open_offcanvas"><img src="/assets/imgs/icon/menu-black.png" alt="Menubar Icon" /></button>
                    </div>
                </div>
            </header>

            <Canvas />

            <div id="smooth-wrapper">
                <div id="smooth-content">
                    <main>
                        {/* Hero Section */}
                        <section className="project-hero">
                            <Link to="/our-work" className="back-btn">
                                <i className="fa-solid fa-arrow-left"></i>
                            </Link>
                            <div className="container">
                                <div className="hero-layout-grid">
                                    <div className="hero-content">
                                        <span className="project-tagline">{project.hero.tagline}</span>
                                        <h1 className="project-title">
                                            {project.hero.mainTitle}
                                            {project.websiteLink && (
                                                <a href={project.websiteLink} target="_blank" rel="noopener noreferrer" className="title-arrow-link">
                                                    <i className="fa-solid fa-arrow-up-right-from-square animated-arrow"></i>
                                                </a>
                                            )}
                                        </h1>
                                        <p className="project-narrative">{project.hero.narrative}</p>
                                        <p className="project-subtitle-extended">{project.hero.subtitleExtended}</p>
                                    </div>

                                    {project.showcase && (
                                        <div className="hero-showcase">
                                            <div className="showcase-laptop">
                                                <div className="laptop-frame">
                                                    <img src={`/${project.showcase.laptop}`} alt="Laptop Showcase" />
                                                </div>
                                            </div>
                                            <div className="showcase-mobile">
                                                <div className="mobile-frame">
                                                    <img src={`/${project.showcase.mobile}`} alt="Mobile Showcase" />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* Metadata Section */}
                        <section className="project-metadata">
                            <div className="container">
                                <div className="metadata-grid">
                                    <div className="meta-item">
                                        <label>Role</label>
                                        <span>{project.metadata.role}</span>
                                    </div>
                                    <div className="meta-item">
                                        <label>Duration</label>
                                        <span>{project.metadata.duration}</span>
                                    </div>
                                    <div className="meta-item">
                                        <label>Client</label>
                                        <span>{project.metadata.client}</span>
                                    </div>
                                    <div className="meta-item">
                                        <label>Industry</label>
                                        <span>{project.metadata.industry}</span>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Overview Section */}
                        <section className="project-overview">
                            <div className="container">
                                <div className="overview-header-group">
                                    <span className="overview-subheadline">The Challenge</span>
                                    <h2 className="overview-headline">{project.overview.theProblem.headline}</h2>
                                </div>

                                <div className="overview-layout-grid">
                                    <div className="challenge-column">
                                        <div className="problem-grid">
                                            {project.overview.theProblem.details.split('\n')
                                                .filter((line: string) => line.trim().startsWith('-'))
                                                .map((item: string, index: number) => (
                                                    <div key={index} className="problem-card">
                                                        <div className="problem-card-inner">
                                                            <div className="problem-card-icon">
                                                                <i className="fa-solid fa-circle-exclamation"></i>
                                                            </div>
                                                            <p>{item.replace(/^- /, '').trim()}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                        {project.overview.theProblem.details.split('\n')
                                            .filter((line: string) => !line.trim().startsWith('-') && line.trim().length > 0)
                                            .map((text: string, index: number) => (
                                                <p key={`text-${index}`} className="challenge-intro-text">{text}</p>
                                            ))}
                                    </div>

                                    <div className="solution-column">
                                        <div className="solution-card">
                                            <div className="solution-inner">
                                                <div className="solution-tag">THE SOLUTION</div>
                                                <h3 className="solution-title">Empowering Growth through Innovation</h3>
                                                <p className="solution-text">{project.overview.theSolution}</p>
                                                <div className="solution-features">
                                                    <div className="s-feat"><i className="fa-solid fa-bolt"></i> Speed</div>
                                                    <div className="s-feat"><i className="fa-solid fa-shield"></i> Security</div>
                                                    <div className="s-feat"><i className="fa-solid fa-chart-simple"></i> Scale</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Target Users */}
                        <section className="target-users">
                            <div className="container">
                                <h2>Target Users & Roles</h2>
                                <div className="roles-grid">
                                    {project.overview.targetUsersAndRoles.map((role: any, index: number) => (
                                        <div key={index} className="role-card">
                                            <h4>{role.role}</h4>
                                            <p>{role.responsibilities}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* Key Features */}
                        <section className="key-features">
                            <div className="container">
                                <h2>Key Features</h2>
                                <div className="features-list">
                                    {project.keyFeatures.map((feature: string, index: number) => (
                                        <div key={index} className="feature-item">
                                            <i className="fa-solid fa-check-circle"></i>
                                            <p>{feature}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* Development Journey */}
                        <section className="development-journey">
                            <div className="container">
                                <h2>Development Journey</h2>
                                <div className="timeline">
                                    {project.developmentJourney.map((phase: any, index: number) => (
                                        <div key={index} className="timeline-item">
                                            <div className="timeline-header">
                                                <span className="phase-title">{phase.phase}</span>
                                                <span className="phase-duration">{phase.duration}</span>
                                            </div>
                                            <p>{phase.keyActivities}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* Tech Stack */}
                        <section className="tech-stack-section">
                            <div className="container">
                                <div className="section-header">
                                    <h2 className="section-title">Architecture & Tech</h2>
                                    <div className="principles-grid">
                                        {project.architectureAndTech.coreDesignPrinciples.split('.')
                                            .filter((p: string) => p.trim().length > 0)
                                            .map((principle: string, index: number) => {
                                                const parts = principle.includes('→') ? principle.split('→') : principle.split('->');
                                                const title = parts[0];
                                                const desc = parts[1];
                                                return (
                                                    <div key={index} className="principle-card">
                                                        <div className="p-card-top">
                                                            <div className="p-icon">
                                                                <i className={index === 0 ? "fa-solid fa-cube" : index === 1 ? "fa-solid fa-robot" : "fa-solid fa-file-shield"}></i>
                                                            </div>
                                                            <h4>{title.trim()}</h4>
                                                        </div>
                                                        <p>{desc?.trim()}</p>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>

                                <div className="tech-dashboard">
                                    <div className="tech-groups">
                                        {Object.entries(project.architectureAndTech.techStack).map(([key, value]: [string, any], index) => (
                                            <div key={index} className="tech-group-card">
                                                <div className="tech-group-label">{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                                                <div className="tech-group-value">{value}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="security-compliance-box">
                                    <div className="compliance-header">
                                        <div className="compliance-icon-wrap">
                                            <i className="fa-solid fa-shield-halved"></i>
                                        </div>
                                        <div className="compliance-title-group">
                                            <h4>Security & Compliance</h4>
                                            <p>Enterprise-grade safety standards</p>
                                        </div>
                                    </div>
                                    <div className="compliance-body">
                                        {project.architectureAndTech.securityComplianceHighlights}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Impact Section */}
                        <section className="impact-results">
                            <div className="container">
                                <h2>Impact & Results</h2>
                                <div className="impact-grid">
                                    <div className="quantitative">
                                        <h4>Quantitative</h4>
                                        {project.impactAndResults.quantitative.map((item: string, index: number) => (
                                            <div key={index} className="impact-stat-item">
                                                <i className="fa-solid fa-chart-line"></i>
                                                <span>{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="qualitative">
                                        <h4>Qualitative</h4>
                                        {project.impactAndResults.qualitative.map((item: string, index: number) => (
                                            <div key={index} className="impact-stat-item">
                                                <i className="fa-solid fa-star"></i>
                                                <span>{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="lessons-learned">
                                    <div className="lessons-header">
                                        <div className="l-icon-wrap"><i className="fa-solid fa-lightbulb"></i></div>
                                        <h4>Lessons Learned</h4>
                                    </div>
                                    <div className="lessons-grid">
                                        {project.impactAndResults.lessonsLearned.split('.')
                                            .filter((l: string) => l.trim().length > 0)
                                            .map((lesson: string, index: number) => (
                                                <div key={index} className="lesson-card">
                                                    <div className="l-count">0{index + 1}</div>
                                                    <p>{lesson.trim()}.</p>
                                                </div>
                                            ))}
                                    </div>
                                    {project.websiteLink && (
                                        <div className="mt-5" style={{ textAlign: 'center' }}>
                                            <a href={project.websiteLink} target="_blank" rel="noopener noreferrer" className="moving-btn">
                                                Visit Live Website <i className="fa-solid fa-arrow-right"></i>
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>

                        {/* Blogs Section */}
                        <section className="project-gallery">
                            <div className="container">
                                <h2>Blogs</h2>
                                <div className="gallery-grid">
                                    {blogData.map((blog, index) => (
                                        <Link
                                            key={`blog-${index}`}
                                            to={`/our-work/blog/${blog.slug}`}
                                            state={{ from: location.pathname }}
                                            className="blog-card-mini"
                                        >
                                            <div className="blog-card-mini-image">
                                                <img src={`/${blog.thumbnail.image}`} alt={blog.thumbnail.alt} />
                                            </div>
                                            <div className="blog-card-mini-content">
                                                <h5>{blog.title}</h5>
                                                <p>{blog.excerpt}</p>
                                                <span className="read-more">Read More <i className="fa-solid fa-arrow-right"></i></span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* CTA Section */}
                        <section className="project-cta">
                            <div className="container">
                                <p className="cta-statement">{project.cta.statement}</p>
                                <a href="https://calendly.com/sacesta/30min" className="btn-primary">Start Your Project</a>
                            </div>
                        </section>
                    </main>

                    <MainFooter />
                </div>
            </div>
        </div>
    );
};

export default ProjectDetail;
